import React, { useEffect, useState } from "react";
import { getById, create, update } from "../../services/role.service";
import {useParams, useNavigate, NavigateFunction} from "react-router-dom";
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {TextField, Link} from "@mui/material";
import { GridSaveAltIcon } from "@mui/x-data-grid";
import { ArrowBackIosNew } from "@mui/icons-material";
import showNotification from "../utils/Notify";
import {Box, Grid, Typography, Button, Stack, Breadcrumbs, List, ListItem, Checkbox} from "@mui/joy";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import IRole from "../../types/role.type";
import IPermission from "../../types/permission.type";
import {getPermissions} from "../../services/permission.service";

const RolesForm: React.FC = () => {
  let navigate: NavigateFunction = useNavigate();
  const { id } = useParams();
  const [permissions, setPermissions] = useState<Array<IPermission>>([]);

  const initialValues: IRole = {
    id: null,
    name: '',
    description: '',
    permissionIds: [],
  };

  const [role, setRole] = useState(initialValues);

  const validationSchema = Yup.object({
    name: Yup.string().required('El nombre del Rol es un campo requerido'),
    permissionIds: Yup.array().min(1,"Debe seleccionar al menos un permiso para el Rol"),
  });

  const onSubmit = (owner: IRole) => {
    if (id) {
      update(owner.id, owner)
        .then(() => {
          showNotification({type: 'success', message: 'Owner updated successfully.'});
          goBack();
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    } else {
      create(owner)
        .then((response) => {
          showNotification({type: 'success', message: 'Owner created successfully.'});
          goBack();
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    onSubmit,
    validationSchema: validationSchema,
  });

  const handleOnChangeCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    permissions[permissions.findIndex(i => i.id == event.target.value)].checked = event.target.checked;
    formik.setFieldValue('permissionIds', permissions.filter(i => i.checked).map(i => i.id));
  };

  const goBack = () => {
    navigate(-1)
  };

  const fetchRole = async () => {
    if (id) {
      getById(id).then( (response) => {
        let roleData = response.data;
        formik.setValues(roleData)
        setRole(roleData);
        getPermissions().then( (response) => {
          let permissionsData = response.data;
          roleData.permissionIds.forEach((id: any) => permissionsData[permissionsData.findIndex((i: { id: any; }) => i.id == id)].checked = true)
          setPermissions(permissionsData);
          formik.setFieldValue('permissionIds', permissionsData.filter((i: { checked: any; }) => i.checked).map((i: { id: any; }) => i.id));
        })
      })
    } else {
      getPermissions().then( (response) => {
        setPermissions(response.data);
      })
    }
  };

  useEffect(() => {
    fetchRole();
  }, []);

  return (
    <Box
      sx={{
        px: { xs: 2},
        pt: { xs: 'calc(12px + var(--Header-height))' },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100dvh',
        overflow: "auto"
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', px: { xs: 1} }}>
            <Breadcrumbs
              size="sm"
              aria-label="breadcrumbs"
              separator={<ChevronRightRoundedIcon/>}
              sx={{ pl: 0 }}
            >
              <Typography level="h4" component="h1">
                <AdminPanelSettingsIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} />
              </Typography>
              <Typography level="h4" component="h1">
                <Link
                  underline={"none"}
                  onClick={() => goBack()}
                  sx={{ cursor: 'pointer' }}
                >
                  Roles y Permisos
                </Link>
              </Typography>
              <Typography level="h4" component="h1">
                {id ? "Editar" : "Nuevo"}
              </Typography>
            </Breadcrumbs>
          </Box>
          <Box sx={{ width: '100%', typography: 'body1' }}>
            <form className="form" onSubmit={formik.handleSubmit}>
              <Grid
                container
                rowSpacing={2}
                columnSpacing={2}
                marginBlock={1}
                marginInline={0}
                sx={{ width: '100%' }}
              >
                <Grid xl={12} md={12} sm={12} xs={12}>
                  <CssVarsProvider>
                    <TextField
                      fullWidth
                      id="name"
                      label="Nombre"
                      name="name"
                      variant="filled"
                      type={"text"}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.name}
                      slotProps={{ inputLabel: {shrink: true}}}
                      error={!!(formik.touched.name && formik.errors.name)}
                    />
                    {formik.touched.name && formik.errors.name ? (
                      <span className="Error-alert">{formik.errors.name}</span>
                    ) : null}
                  </CssVarsProvider>
                </Grid>
                <Grid xl={12} md={12} sm={12} xs={12}>
                  <CssVarsProvider>
                    <TextField
                      fullWidth
                      id="description"
                      label="Descripcion"
                      name="description"
                      variant="filled"
                      type={"text"}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.description}
                      error={!!(formik.touched.description && formik.errors.description)}
                      slotProps={{ inputLabel: {shrink: true}}}
                      multiline
                      rows={3}
                    />
                  </CssVarsProvider>
                </Grid>

                <Grid xl={12} md={12} sm={12} xs={12}>
                  <Typography level="h4" component="h1">
                    Permisos:
                  </Typography>
                  {formik.touched.permissionIds && formik.errors.permissionIds ? (
                    <span className="Error-alert">{formik.errors.permissionIds}</span>
                  ) : null}
                  <List>
                    {permissions?.map((permission) => (
                      <ListItem variant="plain" color="warning" sx={{ borderRadius: 'sm' }}>
                        <Checkbox
                          id={permission.id}
                          label={permission.description}
                          value={permission.id}
                          color="neutral"
                          overlay
                          checked={permission.checked}
                          onChange={handleOnChangeCheckbox}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>


              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button onClick={() => goBack()} variant="outlined" endDecorator={<ArrowBackIosNew />}>Volver</Button>
                <Button type="submit" variant="solid" endDecorator={<GridSaveAltIcon />}>Guardar</Button>
              </Stack>
            </form>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RolesForm;
